import { useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "cloudisian-cookie-consent";

const defaultPreferences = {
  essential: true,      // always true, cannot be disabled
  analytics: false,
  marketing: false,
  preferences: false,
};

export function useCookieConsent() {
  const [consentGiven, setConsentGiven] = useState(null); // null = not decided yet
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Load saved preferences and show the banner on every fresh Home page load.
  // Once opened, it stays active across client-side navigation until an option
  // is selected; the selection hides it for the remainder of that page load.
  useEffect(() => {
    const isHomePage = window.location.pathname === "/";
    const timer = isHomePage
      ? setTimeout(() => setShowBanner(true), 800)
      : null;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setConsentGiven(true);
        setPreferences({ ...defaultPreferences, ...parsed, essential: true });
      }
    } catch {
      /* ignore invalid or unavailable storage */
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  const saveConsent = useCallback((prefs) => {
    const toSave = { ...prefs, essential: true };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch { /* ignore */ }
    setPreferences(toSave);
    setConsentGiven(true);
    setShowBanner(false);
    setShowModal(false);
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ essential: true, analytics: true, marketing: true, preferences: true });
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    saveConsent({ essential: true, analytics: false, marketing: false, preferences: false });
  }, [saveConsent]);

  const savePreferences = useCallback((prefs) => {
    saveConsent(prefs);
  }, [saveConsent]);

  const openModal = useCallback(() => setShowModal(true), []);
  const closeModal = useCallback(() => setShowModal(false), []);

  const reopenSettings = useCallback(() => {
    setShowModal(true);
  }, []);

  return {
    consentGiven,
    preferences,
    showBanner,
    showModal,
    acceptAll,
    rejectAll,
    savePreferences,
    openModal,
    closeModal,
    reopenSettings,
  };
}
